import React, { useState, useEffect, useRef } from "react";
import {
  ChevronDown,
  ChevronRight,
  List,
  GripVertical,
  Braces,
} from "lucide-react";
import { AvailableVariable } from "../ui/types";

interface NodeDataSidebarProps {
  availableVariables: AvailableVariable[];
  onDragStart: (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output"
  ) => void;
}

export const NodeDataSidebar: React.FC<NodeDataSidebarProps> = ({
  availableVariables,
  onDragStart,
}) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const previousNodeIds = useRef<string>("");

  // Expand only the last node (previous node) by default when availableVariables changes
  useEffect(() => {
    if (availableVariables.length > 0) {
      const currentNodeIds = availableVariables.map((v) => v.nodeId).join(",");

      // Only reset expansion if the list of available nodes has actually changed
      if (currentNodeIds !== previousNodeIds.current) {
        const lastNode = availableVariables[availableVariables.length - 1];
        setExpandedNodes(new Set([lastNode.nodeId]));
        previousNodeIds.current = currentNodeIds;
      }
    }
  }, [availableVariables]);

  const toggleNodeExpansion = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      newSet.has(nodeId) ? newSet.delete(nodeId) : newSet.add(nodeId);
      return newSet;
    });
  };

  const togglePathExpansion = (pathId: string) => {
    setExpandedPaths((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pathId)) {
        newSet.delete(pathId);
      } else {
        newSet.add(pathId);
      }
      return newSet;
    });
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return "null";
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    if (typeof value === "boolean") return value ? "true" : "false";
    return String(value);
  };

  // Helper function to render nested objects and arrays
  const renderNestedOutputs = (
    obj: any,
    nodeData: AvailableVariable,
    parentKey: string = "",
    level: number = 0
  ) => {
    const items: JSX.Element[] = [];

    Object.entries(obj).forEach(([key, value]) => {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      const pathId = `${nodeData.nodeId}-${fullKey}`;
      const isExpanded = expandedPaths.has(pathId);

      if (Array.isArray(value)) {
        // Array - show as expandable parent
        const hasItems = value.length > 0;

        items.push(
          <div key={fullKey} className="space-y-0.5">
            <div
              draggable
              onDragStart={(e) =>
                onDragStart(e, fullKey, nodeData.nodeName, "output")
              }
              onClick={() => hasItems && togglePathExpansion(pathId)}
              className={`group flex items-center gap-2 py-1.5 px-2 rounded ${
                hasItems ? "cursor-pointer hover:bg-gray-100" : ""
              } transition-colors`}
            >
              <GripVertical className="w-3 h-3 text-slate-300 group-hover:text-gray-500 transition-colors shrink-0" />
              {hasItems ? (
                isExpanded ? (
                  <ChevronDown className="w-3 h-3 text-gray-500 shrink-0" />
                ) : (
                  <ChevronRight className="w-3 h-3 text-gray-500 shrink-0" />
                )
              ) : (
                <div className="w-3 h-3 shrink-0" />
              )}
              <div className="w-1.5 h-1.5 bg-purple-500 rounded-full shrink-0"></div>
              <span className="text-[11px] font-medium text-gray-700 truncate">
                {key}
              </span>
              <span className="text-[9px] text-gray-500 bg-purple-50 px-1.5 py-0.5 rounded shrink-0">
                [{value.length}]
              </span>
            </div>

            {isExpanded && hasItems && (
              <div className="ml-5 space-y-0.5">
                {value.map((item, index) => {
                  const arrayKey = `${fullKey}[${index}]`;
                  const arrayPathId = `${nodeData.nodeId}-${arrayKey}`;
                  const isArrayExpanded = expandedPaths.has(arrayPathId);
                  const isObject = item && typeof item === "object";

                  if (isObject) {
                    // Array item is an object - show as expandable
                    return (
                      <div key={arrayKey} className="space-y-0.5">
                        <div
                          onClick={() => togglePathExpansion(arrayPathId)}
                          className="flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer hover:bg-purple-50 transition-colors"
                        >
                          {isArrayExpanded ? (
                            <ChevronDown className="w-3 h-3 text-gray-500 shrink-0" />
                          ) : (
                            <ChevronRight className="w-3 h-3 text-gray-500 shrink-0" />
                          )}
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full shrink-0"></div>
                          <span className="text-[11px] font-medium text-purple-700">
                            [{index}]
                          </span>
                        </div>

                        {isArrayExpanded && (
                          <div className="ml-5 space-y-0.5">
                            {renderNestedOutputs(
                              item,
                              nodeData,
                              arrayKey,
                              level + 1
                            )}
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    // Array item is a primitive - draggable
                    return (
                      <div
                        key={arrayKey}
                        draggable
                        onDragStart={(e) =>
                          onDragStart(e, arrayKey, nodeData.nodeName, "output")
                        }
                        className="group flex items-center gap-2 py-1.5 px-2 rounded-md bg-white border border-slate-200 hover:border-purple-300 hover:shadow-sm hover:bg-purple-50/50 cursor-grab active:cursor-grabbing transition-all duration-200"
                      >
                        <GripVertical className="w-3 h-3 text-slate-300 group-hover:text-purple-400 transition-colors shrink-0" />
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full shrink-0"></div>
                        <span className="text-[11px] text-gray-700 truncate flex-1 font-medium">
                          [{index}]
                        </span>
                        <span className="text-[10px] text-gray-400 group-hover:text-purple-500 truncate max-w-[80px]">
                          {formatValue(item)}
                        </span>
                      </div>
                    );
                  }
                })}
              </div>
            )}
          </div>
        );
      } else if (value && typeof value === "object") {
        // Nested object - show as expandable parent
        const hasChildren = Object.keys(value).length > 0;

        items.push(
          <div key={fullKey} className="space-y-0.5">
            <div
              draggable
              onDragStart={(e) =>
                onDragStart(e, fullKey, nodeData.nodeName, "output")
              }
              onClick={() => hasChildren && togglePathExpansion(pathId)}
              className={`group flex items-center gap-2 py-1.5 px-2 rounded ${
                hasChildren ? "cursor-pointer hover:bg-gray-100" : ""
              } transition-colors`}
            >
              <GripVertical className="w-3 h-3 text-slate-300 group-hover:text-gray-500 transition-colors shrink-0" />
              {hasChildren ? (
                isExpanded ? (
                  <ChevronDown className="w-3 h-3 text-gray-500 shrink-0" />
                ) : (
                  <ChevronRight className="w-3 h-3 text-gray-500 shrink-0" />
                )
              ) : (
                <div className="w-3 h-3 shrink-0" />
              )}
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full shrink-0"></div>
              <span className="text-[11px] font-medium text-gray-700 truncate">
                {key}
              </span>
            </div>

            {isExpanded && hasChildren && (
              <div className="ml-5 space-y-0.5">
                {renderNestedOutputs(value, nodeData, fullKey, level + 1)}
              </div>
            )}
          </div>
        );
      } else {
        // Leaf node - draggable item
        items.push(
          <div
            key={fullKey}
            draggable
            onDragStart={(e) =>
              onDragStart(e, fullKey, nodeData.nodeName, "output")
            }
            className="group flex items-center gap-2 py-1.5 px-2 rounded-md bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-sm hover:bg-emerald-50/50 cursor-grab active:cursor-grabbing transition-all duration-200"
          >
            <GripVertical className="w-3 h-3 text-slate-300 group-hover:text-emerald-400 transition-colors shrink-0" />
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full shrink-0"></div>
            <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
              <span className="text-[11px] font-medium text-gray-700 truncate">
                {key}
              </span>
              <span className="text-[10px] text-gray-400 group-hover:text-emerald-600 truncate max-w-[100px]">
                {formatValue(value)}
              </span>
            </div>
          </div>
        );
      }
    });

    return items;
  };

  return (
    <div className="w-full bg-gray-50/50 overflow-y-auto p-3 h-full [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
      {availableVariables.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded text-center p-4">
          <List className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-xs text-gray-500 font-medium">
            No connected nodes
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Connect nodes to use their data
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {availableVariables.map((nodeData, index) => {
          const isExpanded = expandedNodes.has(nodeData.nodeId);
          const hasOutputs = Object.keys(nodeData.outputs).length > 0;
          const hasInputs = Object.keys(nodeData.inputs).length > 0;

          // Color palette for differentiating nodes
          const nodeColors = [
            { bg: "bg-indigo-500", light: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700" },
            { bg: "bg-amber-500", light: "bg-amber-50", border: "border-amber-200", text: "text-amber-700" },
            { bg: "bg-rose-500", light: "bg-rose-50", border: "border-rose-200", text: "text-rose-700" },
            { bg: "bg-teal-500", light: "bg-teal-50", border: "border-teal-200", text: "text-teal-700" },
            { bg: "bg-violet-500", light: "bg-violet-50", border: "border-violet-200", text: "text-violet-700" },
            { bg: "bg-cyan-500", light: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-700" },
          ];
          const color = nodeColors[index % nodeColors.length];

          return (
            <div
              key={nodeData.nodeId}
              className={`bg-white border rounded-lg shadow-sm overflow-hidden ${color.border}`}
            >
              <div
                className={`px-3 py-2.5 cursor-pointer flex items-center justify-between transition-colors ${color.light} hover:opacity-90`}
                onClick={() => toggleNodeExpansion(nodeData.nodeId)}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${color.bg}`} />
                  {isExpanded ? (
                    <ChevronDown className={`w-3.5 h-3.5 ${color.text}`} />
                  ) : (
                    <ChevronRight className={`w-3.5 h-3.5 ${color.text}`} />
                  )}
                  <span className={`text-xs font-semibold truncate ${color.text}`}>
                    {nodeData.nodeName}
                  </span>
                </div>
                <span className="text-[10px] text-gray-400 font-medium">
                  #{index + 1}
                </span>
              </div>

              {isExpanded && (
                <div className="px-3 pb-1 pt-2 space-y-1 max-h-[60vh] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
                  {/* Outputs Section */}
                  {hasOutputs && (
                    <div className="space-y-0.5">
                      <div
                        className="relative group flex items-center gap-2 mb-3 p-2 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-md cursor-grab active:cursor-grabbing hover:shadow-md hover:border-emerald-300 transition-all duration-200"
                        draggable
                        onDragStart={(e) =>
                          onDragStart(e, "", nodeData.nodeName, "output")
                        }
                        title="Drag entire outputs object"
                      >
                        <div className="flex items-center justify-center w-8 h-8 bg-white rounded-md border border-emerald-100 shadow-sm text-emerald-600 group-hover:scale-105 transition-transform">
                          <Braces className="w-4 h-4" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h5 className="text-xs font-bold text-emerald-900 uppercase tracking-wide">
                              Outputs
                            </h5>
                            <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-[9px] font-medium text-emerald-700 border border-emerald-200">
                              JSON
                            </span>
                          </div>
                          <p className="text-[10px] text-emerald-600/80 truncate mt-0.5">
                            Drag to use all data
                          </p>
                        </div>

                        <div className="text-emerald-300 group-hover:text-emerald-500 transition-colors">
                          <GripVertical className="w-4 h-4" />
                        </div>
                      </div>
                      {renderNestedOutputs(nodeData.outputs, nodeData)}
                    </div>
                  )}

                  {/* Inputs Section */}
                  {hasInputs && (
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5 mb-2 pb-1 border-b border-blue-100 mt-3">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                        <h5 className="text-[10px] font-bold text-gray-700 uppercase tracking-wide">
                          Inputs
                        </h5>
                      </div>
                      <div className="space-y-0.5">
                        {Object.entries(nodeData.inputs).map(([key, value]) => (
                          <div
                            key={key}
                            draggable
                            onDragStart={(e) =>
                              onDragStart(e, key, nodeData.nodeName, "input")
                            }
                            className="flex items-center gap-2 py-1.5 px-2 rounded bg-white border border-gray-200 hover:bg-blue-50 hover:border-blue-300 cursor-move transition-colors"
                          >
                            <div className="w-3 h-3 shrink-0" />
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full shrink-0"></div>
                            <div className="flex-1 min-w-0">
                              <span className="text-[11px] font-medium text-gray-700 block truncate">
                                {key}
                              </span>
                              <span className="text-[10px] text-gray-500 block truncate">
                                {formatValue(value)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!hasOutputs && !hasInputs && (
                    <p className="text-xs text-gray-400 text-center py-2">
                      No data available
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
        </div>
      )}
    </div>
  );
};
